import { useEffect, useState, type ReactNode } from 'react';
import { handled, toCollect, type Courier, type Stop } from '@day';
import { current, keyOf, type Model, type Person } from '../data/model';
import { askedFor, distance, money, reasonWords, time } from '../app/words';
import { Icon } from './Icon';
import { photoFailed, photoToTry, photoWait } from './photos';

/**
 * A stop's pieces, the same wherever it shows (control-room-web.html: statusOf, tpill, payChip, facts, statusNow,
 * statusIcon): neutral for what it is, tinted for what happened.
 */

/** A courier: their photo ringed in their colour, or their initial on it until they add one (names-photos-invites.html). */
export function Av({ p, size = '', faded = false }: { p: (Pick<Person, 'hex' | 'on' | 'first'> & { courier?: Pick<Courier, 'photoUrl'> }) | null; size?: '' | 'xs' | 'sm' | 'lg' | 'xl'; faded?: boolean }) {
  const c = p ?? { hex: '#5E594F', on: '#FFFFFF', first: '?', courier: undefined };
  const url = c.courier?.photoUrl ?? null;
  const [, retry] = useState(0);
  const photo = photoToTry(url);
  // A photo that wouldn't load is tried again after a while (photos.ts), not left as the initial until a reload.
  useEffect(() => {
    if (!url || photo) return;
    const t = setTimeout(() => retry((n) => n + 1), photoWait(url) + 50);
    return () => clearTimeout(t);
  }, [url, photo]);
  return (
    <span className={`av${size ? ` ${size}` : ''}${faded ? ' faded' : ''}${photo ? ' pic' : ''}`} style={{ '--c': c.hex, '--on': c.on } as React.CSSProperties}>
      {photo ? (
        <img
          src={photo}
          alt=""
          onError={() => {
            photoFailed(photo);
            retry((n) => n + 1);
          }}
        />
      ) : (
        c.first.slice(0, 1).toUpperCase()
      )}
    </span>
  );
}

export const Dot = ({ p }: { p: Pick<Person, 'hex'> | null }) => <span className="dot" style={{ '--c': p?.hex ?? '#5E594F' } as React.CSSProperties} />;

/** Minutes late: an unhandled stop now expected after its deliver-by time. */
export const lateBy = (s: Stop) => (!handled(s) && s.eta != null && s.deliverBy != null && s.eta > s.deliverBy ? Math.round((s.eta - s.deliverBy) / 60000) : 0);

export interface Status {
  icon: string;
  tone: '' | 'pos' | 'neg' | 'cau' | 'ink2';
  word: string;
}

export function statusOf(s: Stop, today: string): Status {
  switch (s.state) {
    case 'done':
      return { icon: 'checkCircleFill', tone: 'pos', word: s.type === 'pickup' ? 'Picked up' : 'Delivered' };
    case 'failed':
      return s.reason === 'anotherDay'
        ? { icon: 'calendarBlank', tone: 'cau', word: `Another day · ${askedFor(s.movedTo, today)}` }
        : { icon: 'xCircleFill', tone: 'neg', word: `Not delivered · ${reasonWords(s.reason)}` };
    case 'current':
      return lateBy(s) ? { icon: 'clock', tone: 'neg', word: 'Running late' } : { icon: 'arrowCircleRightFill', tone: '', word: 'On the way' };
    case 'ahead':
      return { icon: 'arrowCircleRight', tone: '', word: 'To go' };
    case 'waiting':
      return { icon: 'circleDashed', tone: '', word: 'Waiting to start' };
    case 'left':
      return { icon: 'minus', tone: 'ink2', word: 'Not attempted' };
  }
}

/** One time pill for a stop, wherever it shows. `short`: a tight list, with the "about" on hover. */
export function TPill({ s, zone, short }: { s: Stop; zone: string; short?: boolean }) {
  if (s.state === 'done')
    return (
      <span className="tpill done">
        <Icon name="check" size="sm" />
        {time(s.at, zone)}
      </span>
    );
  if (s.state === 'failed')
    return (
      <span className="tpill fail">
        <Icon name="x" size="sm" />
        {time(s.at, zone)}
      </span>
    );
  const ifNow = s.state === 'waiting' ? current.m?.ifNow.stops.get(keyOf(s)) : undefined;
  if (s.eta == null && ifNow != null)
    return (
      <span className="tpill" data-tip="An estimate, if the route leaves now">
        {short ? '' : 'about '}
        {time(ifNow, zone)}
      </span>
    );
  if (s.eta == null)
    return (
      <span className="tpill none" data-tip={s.state === 'left' ? 'The route ended before this stop' : s.state === 'waiting' ? 'No time until the route starts' : 'No time from the courier’s phone yet'}>
        <Icon name="clock" size="sm" />
      </span>
    );
  if (short) return <span className={`tpill${lateBy(s) ? ' late' : ''}`} data-tip={`Expected about ${time(s.eta, zone)}`}>{time(s.eta, zone)}</span>;
  return <span className={`tpill${lateBy(s) ? ' late' : ''}`}>about {time(s.eta, zone)}</span>;
}

export function PayChip({ s, m }: { s: Stop; m: Model }) {
  const amount = toCollect(s);
  if (!amount) return null;
  const cur = s.currency ?? m.currency;
  const first = m.person(s.riderId)?.first ?? 'the courier';
  const paidAmount = s.paidAmount ?? amount;
  if (s.paidHow === 'unpaid')
    return (
      <span className="chip neg">
        <Icon name="money" size="sm" />
        {money(amount, cur)} not paid
      </span>
    );
  if (s.paidHow && s.paidToCourier)
    return (
      <span className="chip" data-tip={`By ${s.paidHow === 'pos' ? 'POS' : s.paidHow}, into ${first}’s own account`}>
        <Icon name="money" size="sm" />
        {money(paidAmount, cur)} paid to {first}
      </span>
    );
  if (s.paidHow)
    return (
      <span className="chip">
        <Icon name="money" size="sm" />
        {money(paidAmount, cur)} paid · {s.paidHow === 'pos' ? 'POS' : s.paidHow}
      </span>
    );
  return (
    <span className="chip">
      <Icon name="money" size="sm" />
      {money(amount, cur)} to collect
    </span>
  );
}

/** Whether a delivered stop has no photo (a handover never has one). */
export const noPhoto = (s: Stop) => s.state === 'done' && s.type === 'delivery' && !s.handover && s.photos.length === 0;

/** The facts about a stop, for its row on a route. A stop already handled shows only what went wrong; its record keeps the rest. */
export function Facts({ s, m }: { s: Stop; m: Model }) {
  const late = lateBy(s);
  const out: ReactNode[] = [];
  const routine = !handled(s);
  if (late)
    out.push(
      <span key="late" className="chip neg">
        <Icon name="clock" size="sm" />
        {late} min late for {time(s.deliverBy, m.zone)}
      </span>,
    );
  else if (s.deliverBy != null && !handled(s))
    out.push(
      <span key="by" className="chip">
        <Icon name="clock" size="sm" />
        By {time(s.deliverBy, m.zone)}
      </span>,
    );
  if (routine && s.type === 'pickup')
    out.push(
      <span key="pickup" className="chip">
        <Icon name="arrowUp" size="sm" />
        Pickup
      </span>,
    );
  if (routine && s.packages > 1)
    out.push(
      <span key="pkgs" className="chip">
        <Icon name="package" size="sm" />
        {s.packages} packages
      </span>,
    );
  if (toCollect(s) && (routine || s.paidHow === 'unpaid')) out.push(<PayChip key="pay" s={s} m={m} />);
  if (s.state === 'failed')
    out.push(
      s.reason === 'anotherDay' ? (
        <span key="why" className="chip cau">
          <Icon name="calendarBlank" size="sm" />
          Another day · {askedFor(s.movedTo, m.today)}
        </span>
      ) : (
        <span key="why" className="chip neg">
          <Icon name="x" size="sm" />
          {reasonWords(s.reason)}
        </span>
      ),
    );
  if (s.away != null && s.away > 300)
    out.push(
      <span key="far" className="chip neg" data-tip="Where the courier’s phone was when they marked it, against the address">
        <Icon name="mapPin" size="sm" />
        Marked {distance(s.away)} away
      </span>,
    );
  if (s.attempts > 0 && s.state !== 'failed')
    out.push(
      <span key="tries" className="chip">
        <Icon name="arrowsClockwise" size="sm" />
        Tried before
      </span>,
    );
  if (noPhoto(s))
    out.push(
      <span key="photo" className="chip">
        <Icon name="cameraSlash" size="sm" />
        No photo
      </span>,
    );
  return out.length ? <>{out}</> : null;
}

/** The tab icons for a stop's status (the Stops page's tabs). */
export const STATUS_TABS: { k: string; l: string; icon: string; f: (s: Stop) => boolean }[] = [
  { k: 'all', l: 'All stops', icon: 'mapPin', f: () => true },
  { k: 'waiting', l: 'Waiting to start', icon: 'circleDashed', f: (s) => s.state === 'waiting' },
  { k: 'togo', l: 'To go', icon: 'arrowCircleRight', f: (s) => s.state === 'current' || s.state === 'ahead' },
  { k: 'done', l: 'Delivered', icon: 'checkCircle', f: (s) => s.state === 'done' },
  { k: 'failed', l: 'Not delivered', icon: 'xCircle', f: (s) => s.state === 'failed' || s.state === 'left' },
];

/** A row's status: its tab's icon, coloured by meaning, with the words on hover. */
export function StatusIcon({ s, today }: { s: Stop; today: string }) {
  const tab = STATUS_TABS.find((t) => t.k !== 'all' && t.f(s)) ?? STATUS_TABS[0];
  const tone = s.state === 'done' ? 'pos' : s.state === 'failed' ? 'neg' : s.state === 'current' ? '' : 'ink2';
  const word = statusOf(s, today).word;
  return (
    <span className={tone} role="img" aria-label={word} data-tip={word}>
      <Icon name={tab.icon} size="lg" />
    </span>
  );
}

const chip = (key: string, text: string, icon?: string, tone = '') => (
  <span key={key} className={`chip${tone ? ` ${tone}` : ''}`}>
    {icon && <Icon name={icon} size="sm" />}
    {text}
  </span>
);

/** What matters about a stop now: at most two chips, most important first. Its record keeps the rest. */
export function StatusNow({ s, m, sameRoute = false, routeName }: { s: Stop; m: Model; sameRoute?: boolean; routeName: string }) {
  const late = lateBy(s);
  const when = late ? <span key="when" className="tpill">about {time(s.eta, m.zone)}</span> : <TPill key="when" s={s} zone={m.zone} />;
  const pay = toCollect(s) ? <PayChip key="pay" s={s} m={m} /> : null;
  const by = s.deliverBy != null ? chip('by', `By ${time(s.deliverBy, m.zone)}`, 'clock', late ? 'cau' : '') : null;
  const load = s.type === 'pickup' ? chip('load', 'Pickup', 'arrowUp') : s.packages > 1 ? chip('load', `${s.packages} packages`, 'package') : null;
  let pair: ReactNode[];
  switch (s.state) {
    case 'waiting':
      pair = sameRoute ? [s.eta != null ? when : null, by, pay, load].filter(Boolean).slice(0, 2) : [chip('route', routeName), by ?? pay ?? load];
      break;
    case 'ahead':
      pair = [when, pay ?? by ?? load];
      break;
    case 'current':
      pair = [when, by ?? pay ?? load];
      break;
    case 'done':
      pair = [
        when,
        (s.paidHow === 'unpaid' && pay) || (s.away != null && s.away > 300 && chip('far', `Marked ${Math.round(s.away / 10) * 10} m away`, 'mapPin', 'neg')) || (noPhoto(s) && chip('photo', 'No photo', 'cameraSlash', 'cau')) || pay || load,
      ];
      break;
    case 'failed':
      pair = [when, s.reason === 'anotherDay' ? chip('why', `Another day · ${askedFor(s.movedTo, m.today)}`, 'calendarBlank', 'cau') : chip('why', reasonWords(s.reason), undefined, 'neg')];
      break;
    case 'left':
      pair = [chip('left', 'Not attempted', 'minus')];
      break;
  }
  return <div className="now-chips">{pair}</div>;
}

/** A route's stops as segments: delivered, not, the one being driven to, the rest. */
export function Segs({ stops }: { stops: Stop[] }) {
  return (
    <div className="segs">
      {stops.map((s) => (
        <i
          key={s.id}
          style={{ background: s.state === 'done' ? 'var(--positive)' : s.state === 'failed' ? 'var(--negative)' : s.state === 'current' ? 'var(--ink3)' : 'var(--muted)' }}
        />
      ))}
    </div>
  );
}

export function More({ menu, label = 'More' }: { menu: string; label?: string }) {
  return (
    <button className="iconbtn sm more" data-menu={menu} aria-label={label}>
      <Icon name="dotsThree" />
    </button>
  );
}

export interface Try {
  icon: string;
  label: string;
  note?: string;
  act: string;
}

/** Never a bare "nothing": say what was searched, then two or three ways forward. */
export function EmptyHelp({ q, title, tries = [], onTry }: { q?: string; title?: string; tries?: Try[]; onTry: (act: string) => void }) {
  return (
    <div className="empty">
      <b>{q ? `Nothing found for “${q}”.` : (title ?? 'Nothing here yet.')}</b>
      {tries.length > 0 && (
        <>
          <div className="eyebrow" style={{ marginTop: 14 }}>
            Try
          </div>
          <div className="try">
            {tries.map((t) => (
              <div
                key={t.act + t.label}
                className="t"
                role="button"
                tabIndex={0}
                onClick={() => onTry(t.act)}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onTry(t.act))}
              >
                <Icon name={t.icon} />
                <div>
                  <b>{t.label}</b>
                  {t.note && <div className="cap">{t.note}</div>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const COMMON = new Set(['street', 'close', 'crescent', 'road', 'avenue', 'way', 'drive']);

/** Close matches for a search that found nothing ("Did you mean"), by edit distance on the words that name things. */
export function nearly<T>(q: string, items: T[], text: (x: T) => string): T[] {
  const named = (w: string) => w.length >= 4 && !COMMON.has(w) && ![...COMMON].some((c) => c.startsWith(w.slice(0, 3)) && Math.abs(c.length - w.length) <= 2);
  const words = q.toLowerCase().split(/[\s,]+/).filter(named);
  if (!words.length) return [];
  const lev = (a: string, b: string) => {
    let row = Array.from({ length: b.length + 1 }, (_, j) => j);
    for (let i = 1; i <= a.length; i++) {
      const next = [i];
      for (let j = 1; j <= b.length; j++) next[j] = Math.min(row[j] + 1, next[j - 1] + 1, row[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      row = next;
    }
    return row[b.length];
  };
  const close = (a: string, b: string) => named(a) && Math.abs(a.length - b.length) <= 2 && lev(a, b) <= (b.length >= 7 ? 2 : 1);
  const score = (x: T) => text(x).toLowerCase().split(/\s+/).filter((w) => words.some((v) => close(w, v))).length;
  return items
    .map((x) => [x, score(x)] as const)
    .filter(([, n]) => n)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([x]) => x);
}

/** A button that isn't wired yet: says so instead of doing nothing (actions come after the user's check). */
export const LATER = 'later';
